import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import "../../../styles/payrole-system/payrole-forms.css";
import { convertThenFormat1 } from "../../common-data/Converts";
import { showConfirmAlert } from "../../components-react/ReactAlert";
import { commonPostPutApi } from "../../../redux/reducers/commonApiSlice";
import {
  fetchIncentivePercentageData,
  fetchTrainerIncentiveByIdData,
} from "../../../redux/payrole-reducers/payroleSlice";

const IncentiveCalculationModal = ({
  userInfoData,
  payrollData,
  onClose,
  callFunction,
}) => {
  const dispatch = useDispatch();
  const { postPutLoading } = useSelector((state) => state.common);
  const {
    incentivePercentageData,
    incentivePercentageLoading,
    incentivePercentageError,

    trainerIncentiveByIdData,
    trainerIncentiveByIdLoading,
    trainerIncentiveByIdError,
  } = useSelector((state) => state.payrole);

  const [incentiveOptions, setIncentiveOptions] = useState([]);

  const iniFormvalues = {
    incentivePercentage: "",
  };
  const [formValues, setFormValues] = useState(iniFormvalues);
  const [formErrors, setFormErrors] = useState({});
  const [calculatedIncentive, setCalculatedIncentive] = useState(null);
  const [isCalculated, setIsCalculated] = useState(false);

  const handleClose = () => {
    setFormValues(iniFormvalues);
    setFormErrors({});
    setCalculatedIncentive(null);
    setIsCalculated(false);
    onClose();
  };

  const validateForm = (values) => {
    const errors = {};

    if (!values.incentivePercentage) {
      errors.incentivePercentage = "Please select an incentive percentage!";
    }

    return errors;
  };

  const calculateIncentive = () => {
    const errors = validateForm(formValues);
    setFormErrors(errors);

    if (Object.keys(errors).length === 0) {
      const dateStr = payrollData?.date || "";
      let month = "";
      let year = "";
      if (dateStr) {
        const parsed = new Date(dateStr);
        if (!isNaN(parsed.getTime())) {
          month = String(parsed.getMonth() + 1);
          year = String(parsed.getFullYear());
        } else {
          const parts = dateStr.split("-");
          if (parts.length >= 2) {
            year = parts[0];
            month = String(parts[1]);
          }
        }
      }

      const sendableFilters = {
        trainer_id: payrollData?.user_id || "",
        month,
        year,
        incentive_percentage: formValues.incentivePercentage || 0,
      };

      getCalculatedIncentive(sendableFilters);
    }
  };

  const calculateTotalSalary = () => {
    const netSalary = parseFloat(payrollData?.net_salary || 0);
    const incentive = parseFloat(calculatedIncentive?.total_incentive || 0);
    return netSalary + incentive;
  };

  const handleAddIncentive = async () => {
    const payload = {
      payrole_id: payrollData?.uid || "",
      incentive_percentage: parseFloat(formValues.incentivePercentage),
      incentive: parseFloat(calculatedIncentive?.total_incentive || 0),
    };

    const handleConfirm = async () => {
      try {
        const result = await dispatch(
          commonPostPutApi({
            method: "POST",
            endpoint: "/payrole/add_pt_incentive",
            payload: payload,
            token: userInfoData?.token || "",
          })
        );

        if (result?.payload?.status) {
          callFunction();
          handleClose();
          toast.success(
            result?.payload?.message || "Incentive added successfully!"
          );
        } else {
          const errorMessage =
            result?.payload?.message || result?.error?.message;
          toast.error(errorMessage || "Something Went Wrong!");
        }
      } catch (error) {
        console.error("Error adding incentive:", error);
        toast.error("Failed to add incentive. Please try again.");
      }
    };

    showConfirmAlert(
      handleConfirm,
      `Are you sure you want to add ₹${convertThenFormat1(
        calculatedIncentive?.total_incentive
      )} incentive (${formValues.incentivePercentage}%) to ${
        payrollData?.name
      }'s payroll?`,
      "No, Cancel",
      "Yes, Add Incentive"
    );
  };

  const handlePercentageChange = (percentage) => {
    setFormValues((prev) => ({
      ...prev,
      incentivePercentage:
        prev.incentivePercentage === percentage ? "" : percentage,
    }));
    setIsCalculated(false);
    setCalculatedIncentive(null);
  };

  const getIncentivePercentage = (filters) => {
    dispatch(
      fetchIncentivePercentageData({
        filters: filters,
        token: userInfoData?.token,
      })
    );
  };

  const getCalculatedIncentive = (filters) => {
    dispatch(
      fetchTrainerIncentiveByIdData({
        filters: filters,
        token: userInfoData?.token,
      })
    );
  };

  useEffect(() => {
    const sendableFilters = {
      page: 1,
      limit: 100,
      status: "active",
    };

    getIncentivePercentage(sendableFilters);
  }, []);

  useEffect(() => {
    if (
      !incentivePercentageLoading &&
      !incentivePercentageError &&
      incentivePercentageData?.status &&
      incentivePercentageData?.data?.length > 0
    ) {
      const percentages = (incentivePercentageData.data || []).map((item) => ({
        value: item?.percentage,
        label: `${item?.percentage}%`,
        info: item,
      }));
      setIncentiveOptions(percentages);
    } else {
      setIncentiveOptions([]);
    }
  }, [
    incentivePercentageLoading,
    incentivePercentageError,
    incentivePercentageData,
  ]);

  useEffect(() => {
    if (
      formValues.incentivePercentage &&
      !trainerIncentiveByIdLoading &&
      !trainerIncentiveByIdError &&
      trainerIncentiveByIdData?.status &&
      Array.isArray(trainerIncentiveByIdData?.data) &&
      trainerIncentiveByIdData.data.length > 0
    ) {
      const result = trainerIncentiveByIdData.data[0];
      setCalculatedIncentive(result);
      setIsCalculated(true);
    } else if (
      formValues.incentivePercentage &&
      !trainerIncentiveByIdLoading &&
      !trainerIncentiveByIdError &&
      trainerIncentiveByIdData?.status &&
      Array.isArray(trainerIncentiveByIdData?.data) &&
      trainerIncentiveByIdData.data.length === 0
    ) {
      setCalculatedIncentive({
        total_incentive: 0,
        base_salary: 0,
        total_salary: 0,
      });
      setIsCalculated(true);
    } else {
      setCalculatedIncentive(null);
      setIsCalculated(false);
    }
  }, [
    trainerIncentiveByIdLoading,
    trainerIncentiveByIdError,
    trainerIncentiveByIdData,
  ]);

  useEffect(() => {
    setFormValues(iniFormvalues);
    setFormErrors({});
    setCalculatedIncentive(null);
    setIsCalculated(false);
  }, [payrollData?.user_id, payrollData?.date]);

  return (
    <div className="pp-approval-container">
      <div className="pp-approval-overlay" onClick={handleClose}></div>
      <div className="pp-approval-form">
        <div className="pp-approval-header">
          <h3 className="pp-approval-title">Calculate Trainer Incentive</h3>
          <button
            type="button"
            className="pp-approval-close-btn"
            onClick={handleClose}
          >
            X
          </button>
        </div>

        <div className="pp-approval-content">
          <div className="pp-approval-employee-card">
            <div className="pp-approval-employee-header">
              <div className="pp-approval-employee-avatar">
                {payrollData?.name?.charAt(0)?.toUpperCase()}
              </div>
              <div className="pp-approval-employee-info">
                <h4 className="pp-approval-employee-name">
                  {payrollData?.name}
                </h4>
                <p className="pp-approval-employee-role">
                  {payrollData?.account_type?.replace(/_/g, " ")}
                </p>
                <p className="pp-approval-employee-gym">
                  {payrollData?.gym_name}
                </p>
              </div>
            </div>
          </div>

          <div className="pp-approval-salary-card">
            <h5 className="pp-approval-card-title">Base Salary Information</h5>
            <div className="pp-approval-salary-grid">
              <div className="pp-approval-salary-item">
                <span className="pp-approval-salary-label">Monthly Salary</span>
                <span className="pp-approval-salary-value">
                  ₹{convertThenFormat1(payrollData?.monthly_salary || 0)}
                </span>
              </div>
              <div className="pp-approval-salary-item">
                <span className="pp-approval-salary-label">Net Salary</span>
                <span className="pp-approval-salary-value">
                  ₹{convertThenFormat1(payrollData?.net_salary || 0)}
                </span>
              </div>
            </div>
          </div>

          <div className="pp-approval-remarks-section">
            <label className="pp-approval-section-label">
              Select Incentive Percentage{" "}
              <span className="pp-approval-required">*</span>
            </label>

            <div className="pp-payroll-group pp-incentive-group">
              {incentiveOptions.map((option) => (
                <label
                  key={option.value}
                  className={`pp-payroll-pill ${
                    formValues.incentivePercentage === option.value
                      ? "selected"
                      : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="incentivePercentage"
                    checked={formValues.incentivePercentage === option.value}
                    onChange={() => handlePercentageChange(option.value)}
                    className="pp-payroll-radio"
                  />
                  {option.label}
                </label>
              ))}
            </div>

            {formErrors?.incentivePercentage && (
              <span className="pp-approval-error">
                {formErrors.incentivePercentage}
              </span>
            )}
          </div>

          {isCalculated && (
            <div className="pp-approval-salary-card">
              <h5 className="pp-approval-card-title">Calculated Incentive</h5>

              {parseFloat(calculatedIncentive?.total_incentive || 0) === 0 ? (
                <div className="pp-no-incentive-message">
                  <div className="pp-no-incentive-icon">💰</div>
                  <h6 className="pp-no-incentive-title">
                    No Incentive Available
                  </h6>
                  <p className="pp-no-incentive-description">
                    Based on the selected {formValues.incentivePercentage}%
                    incentive percentage, no incentive amount is available for
                    this trainer for the selected period.
                  </p>
                </div>
              ) : (
                <div className="pp-approval-salary-grid">
                  <div className="pp-approval-salary-item">
                    <span className="pp-approval-salary-label">
                      Incentive Percentage
                    </span>
                    <span className="pp-approval-salary-value">
                      {formValues.incentivePercentage}%
                    </span>
                  </div>
                  <div className="pp-approval-salary-item">
                    <span className="pp-approval-salary-label">
                      Incentive Amount
                    </span>
                    <span className="pp-approval-salary-value positive">
                      ₹
                      {convertThenFormat1(
                        calculatedIncentive?.total_incentive || 0
                      )}
                    </span>
                  </div>
                  <div className="pp-approval-salary-item">
                    <span className="pp-approval-salary-label">Net Salary</span>
                    <span className="pp-approval-salary-value">
                      ₹{convertThenFormat1(payrollData?.net_salary || 0)}
                    </span>
                  </div>
                  <div className="pp-approval-salary-item">
                    <span className="pp-approval-salary-label">
                      Total Salary
                    </span>
                    <span className="pp-approval-salary-value">
                      ₹{convertThenFormat1(calculateTotalSalary())}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="pp-approval-form-actions">
            {isCalculated ? (
              <>
                <button
                  type="button"
                  className="pp-approval-btn calculate"
                  onClick={calculateIncentive}
                  disabled={
                    !formValues.incentivePercentage ||
                    trainerIncentiveByIdLoading
                  }
                >
                  {trainerIncentiveByIdLoading
                    ? "Recalculating..."
                    : "Re Calculate"}
                </button>

                {parseFloat(calculatedIncentive?.total_incentive || 0) > 0 && (
                  <button
                    type="button"
                    className="pp-approval-btn submit add-incentive"
                    onClick={handleAddIncentive}
                    disabled={postPutLoading}
                  >
                    {postPutLoading ? "Adding..." : "Add Incentive"}
                  </button>
                )}
              </>
            ) : (
              <button
                type="button"
                className="pp-approval-btn calculate"
                onClick={calculateIncentive}
                disabled={
                  !formValues.incentivePercentage || trainerIncentiveByIdLoading
                }
              >
                {trainerIncentiveByIdLoading
                  ? "Calculating..."
                  : "Calculate Incentive"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncentiveCalculationModal;
